import {
  WBBox,
  WBCollapse,
  WBFlex,
  WBIcon,
  WBTypography,
} from '@admiin-com/ds-web';
import { useTranslation } from 'react-i18next';
import AddNewButton from './AddNewButton';
import AddSignatureModal from '../AddSignatureModal/AddSignatureModal';
import React from 'react';
import { useCurrentUser } from '../../hooks/useCurrentUser/useCurrentUser';
import DragDropField from './DragDropField';
import { useTheme, useMediaQuery, Grid, styled } from '@mui/material';
import { Signature } from '@admiin-com/ds-graphql';

export const MySignatures = (props: { onAddClick?: () => void }) => {
  const { t } = useTranslation();
  const [showAddSignatureModal, setShowAddSignatureModal] =
    React.useState(false);
  const [isExpanded, setIsExpanded] = React.useState(false);
  const user = useCurrentUser();
  const signatures = user?.signatures?.items || [];
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const signer = {
    email: user.email ?? '',
    signerType: 'ENTITY_USER' as const,
    id: user.id,
    name: `${user.firstName} ${user.lastName}`,
  };

  const toggleExpand = () => {
    setIsExpanded((prev) => !prev);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleExpand();
    }
  };

  return (
    <Container $isExpanded={isExpanded} $isMobile={isMobile}>
      <CollapsibleHeader
        onClick={toggleExpand}
        onKeyDown={handleKeyDown}
        role="button"
        tabIndex={0}
        aria-expanded={isExpanded}
        aria-controls="my-signatures-content"
      >
        <WBFlex alignItems="center" gap={1} flex={1}>
          <WBTypography fontWeight={'bold'} color="text.secondary">
            {t('signADocumentYourself', { ns: 'taskbox' })}
          </WBTypography>
        </WBFlex>
        <WBIcon
          name={isExpanded ? 'ChevronUp' : 'ChevronDown'}
          color={theme.palette.text.secondary}
        />
      </CollapsibleHeader>
      <WBCollapse in={isExpanded} timeout="auto">
        <WBBox id="my-signatures-content">
          <WBTypography color="text.secondary" mb={1} variant="body2">
            {t('signADocumentYourselfDescription', { ns: 'taskbox' })}
          </WBTypography>
          <Grid container spacing={1}>
            <Grid item xs={12} lg={6}>
              <DragDropField type="DATE" signer={signer} />
            </Grid>
            <Grid item xs={12} lg={6}>
              <DragDropField type="NAME" signer={signer} />
            </Grid>
            <Grid item xs={12} lg={6}>
              <DragDropField type="TEXT" signer={signer} />
            </Grid>
          </Grid>
          <AddNewButton
            name={t('addYourSignature', { ns: 'taskbox' })}
            onClick={() => {
              if (props.onAddClick) props.onAddClick();
              else setShowAddSignatureModal(true);
            }}
          />
          <WBTypography fontWeight={'bold'}>
            {t('savedSignatures', { ns: 'taskbox' })}
          </WBTypography>
          {signatures.length > 0 ? (
            signatures.map(
              (signature: Signature | null, index: number) =>
                signature && (
                  <DragDropField
                    type="SIGNATURE"
                    key={index}
                    name={`${user.firstName} ${user.lastName}`}
                    signature={signature}
                    signer={signer}
                  />
                )
            )
          ) : (
            <WBTypography>
              {t('noSavedSignature', { ns: 'taskbox' })}
            </WBTypography>
          )}
        </WBBox>
      </WBCollapse>

      {!isMobile && (
        <AddSignatureModal
          open={showAddSignatureModal}
          handleClose={() => setShowAddSignatureModal(false)}
        />
      )}
    </Container>
  );
};

const Container = styled(WBFlex)<{ $isExpanded: boolean; $isMobile: boolean }>(
  ({ theme, $isExpanded, $isMobile }) => {
    const horizontalPadding = 4;
    const verticalPadding = $isMobile ? 2 : 3;

    return {
      flexDirection: 'column',
      gap: $isExpanded ? theme.spacing(0.5) : 0,
      padding: $isExpanded
        ? theme.spacing(verticalPadding, horizontalPadding)
        : theme.spacing(0.5, horizontalPadding),
      background: theme.palette.background.paper,
      transition: 'padding 0.3s ease, gap 0.3s ease',
    };
  }
);

const CollapsibleHeader = styled(WBFlex)(({ theme }) => ({
  cursor: 'pointer',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(0.5, 0),
  '&:hover': {
    opacity: 0.8,
  },
}));
